import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";

const title = "Junta de Andalucía and education | SIS Estepona";
const description =
  "Junta de Andalucía is the education authority for Andalusia. How school authorisation, minimum requirements for premises and ongoing inspection work.";

export const Route = createFileRoute("/en/junta-de-andalucia")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/junta-de-andalucia") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/junta-de-andalucia"),
  }),
  component: Junta,
});

function Junta() {
  return (
    <>
      <PageHero
        eyebrow="Supervisory authority"
        title="Junta de Andalucía – the Spanish education authority"
        intro="Junta de Andalucía is the regional government of Andalusia, and its department of education is the authority that authorises and inspects every school operating in the region – roughly the role Skolverket and the Schools Inspectorate have in Sweden."
      />

      <Prose>
        <p>
          Through its department of education (Consejería de Desarrollo Educativo y Formación
          Profesional), Junta de Andalucía works to ensure that all children and young people in
          Andalusia have access to a high-quality, inclusive education – and that every school in
          the region meets the requirements set by law.
        </p>

        <h2>Does this apply to all schools in Spain?</h2>
        <p>
          Yes. Education is regulated nationally but administered by Spain's 17 autonomous regions.
          Every school – public, semi-public (concertado) and fully private – needs administrative
          authorisation from its regional education authority before it may open and operate, and
          all of them are subject to the Spanish education inspectorate, Inspección Educativa. In
          Andalusia that authority is Junta de Andalucía. Schools teaching another country's
          curriculum, like ours, are additionally covered by the specific regulation for foreign
          schools in Spain (Real Decreto 806/1993).
        </p>

        <h2>What supervision means in practice</h2>
        <p>
          <strong>Before a school may open.</strong> Authorisation is granted only when the school
          meets the national minimum requirements in Real Decreto 132/2010, covering the layout and
          floor area per pupil, the size of classrooms and playground, hygiene and safety,
          accessibility, the maximum number of pupils per group and staff qualifications. Any change
          to premises, year groups or places requires a new decision from the authority.
        </p>
        <p>
          <strong>Ongoing inspection.</strong> After authorisation the school is subject to
          Inspección Educativa. The remit is set out in the Spanish education act (LOE, article
          151): to supervise and monitor the school's teaching and organisation, follow up the work
          of teachers and school leadership, check that laws and regulations are complied with, and
          take part in evaluating the education system. Inspectors have the right to visit the
          school, access documentation and speak with staff.
        </p>
        <p>
          <strong>How the work is planned.</strong> In Andalusia the inspectorate works from a
          multi-year plan – Plan General de Actuación 2023–2027 – broken down into annual plans per
          province. Every school has an assigned inspector who is its permanent contact at the
          authority.
        </p>
        <p>
          <strong>Requirements that apply to us.</strong> Beyond premises and safety, the authority
          sets requirements on Spanish language and culture in the teaching. Spanish is therefore part of every school day, from preschool upwards. Our aim is for all Swedish tuition to be taught by teachers holding Swedish teaching qualifications.
        </p>
        <p>
          <strong>What supervision does not cover.</strong> Junta de Andalucía does not review the
          content of the Swedish curriculum. That part rests on Swedish governing documents and on
          our approval from Skolverket for supplementary Swedish – we are not a state-funded Swedish
          school abroad.
        </p>
        <p>
          Sources:{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.boe.es/buscar/act.php?id=BOE-A-2010-4132"
            target="_blank"
            rel="noopener noreferrer"
          >
            Real Decreto 132/2010 (BOE)
          </a>
          ,{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/boja/2023/146/2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Plan General de Actuación 2023–2027 (BOJA)
          </a>{" "}
          and{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/educacion/portales/web/inspeccion-educativa/w/funciones-y-atribuciones"
            target="_blank"
            rel="noopener noreferrer"
          >
            the remit of Inspección Educativa
          </a>
          .
        </p>


        <h2>Education initiatives and programmes</h2>
        <ul>
          <li>
            <strong>Primary and secondary education:</strong> Junta de Andalucía oversees and
            administers primary and secondary schools in the region and ensures schools follow
            national and regional curricula.
          </li>
          <li>
            <strong>Preschool education:</strong> The region offers extensive preschool programmes
            for children aged 0–6 that promote early childhood development.
          </li>
          <li>
            <strong>Special needs education:</strong> Support and resources for pupils with special
            educational needs, including adapted teaching methods.
          </li>
          <li>
            <strong>Digitalisation and innovation:</strong> Digital tools and resources for schools,
            interactive classrooms, learning platforms and training for teachers in digital
            pedagogy.
          </li>
          <li>
            <strong>Teacher training:</strong> Ongoing training and professional development for
            teachers and school leaders.
          </li>
          <li>
            <strong>Cultural and linguistic diversity:</strong> Programmes that support pupils from
            different backgrounds and teaching in several languages.
          </li>
          <li>
            <strong>Health and wellbeing:</strong> Health education, physical activity programmes and
            initiatives for a safe and supportive school environment.
          </li>
        </ul>

        <h2>Collaboration and partnerships</h2>
        <p>
          Junta de Andalucía works closely with schools, municipalities and other educational
          institutions to create a coherent and effective education structure. Swedish International
          School holds a permit and is supervised by Junta de Andalucía.
        </p>
        <p>
          <a href="https://www.juntadeandalucia.es/" target="_blank" rel="noopener noreferrer">
            Read more about Junta de Andalucía here
          </a>
        </p>

        <h2>What this means for Swedish families</h2>
        <p>
          For families moving here it is useful to know that education in Spain is largely handled at
          regional level. Compulsory schooling, the structure of the school year and local public
          holidays follow Andalusian rules, which affects when schools have breaks and how contact with
          the municipality works.
        </p>
        <p>
          Our teaching follows the Swedish curriculum, but we live in the local context: Spanish is on
          the timetable from preschool, and the school calendar takes both Swedish and Andalusian
          holidays into account. That makes the transition easier whether the family stays for years or
          just a period.
        </p>

        <h2>Practical matters when you move</h2>
        <p>
          Moving to Spain involves several administrative steps such as an NIE number, empadronamiento
          and in some cases healthcare registration. We cover what we can in our guide to{" "}
          <a className="text-primary link-quiet" href="/en/nie-and-registration-in-spain">NIE and registration</a>{" "}
          and in the guide to{" "}
          <a className="text-primary link-quiet" href="/en/moving-to-spain-with-children">moving to Spain with children</a>.
          For official decisions we always refer to Junta de Andalucía and your municipality.
        </p>


      </Prose>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
